import type { ThemeConfig } from 'antd';

// Prepwise Backoffice - Warm Ivory Light Theme
// Rich honey amber accents on warm ivory backgrounds

const seedToken: NonNullable<ThemeConfig['token']> = {
  colorPrimary: '#D97706',
  colorPrimaryHover: '#F59E0B',
  colorPrimaryActive: '#B45309',
  borderRadius: 10,
  fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  colorSuccess: '#059669',
  colorWarning: '#D97706',
  colorError: '#DC2626',
  colorInfo: '#2563EB',
  colorBgContainer: '#FFFFFF',
  colorBgElevated: '#FFFFFF',
  colorBgLayout: '#FAFAF8',
  colorBgSpotlight: '#F5F5F3',
  colorBorder: '#EBEBEA',
  colorBorderSecondary: '#F5F5F3',
  colorText: '#1A1A18',
  colorTextSecondary: '#6B6B69',
  colorTextTertiary: '#A8A8A6',
  colorTextQuaternary: '#D4D4D2',
  colorFillSecondary: 'rgba(26, 26, 24, 0.04)',
  colorFillTertiary: 'rgba(26, 26, 24, 0.02)',
};

const buttonToken: NonNullable<NonNullable<ThemeConfig['components']>['Button']> = {
  controlHeightLG: 44,
  controlHeight: 40,
  controlHeightSM: 32,
  paddingInlineLG: 24,
  paddingInline: 18,
  paddingInlineSM: 14,
  borderRadiusLG: 10,
  borderRadius: 8,
  borderRadiusSM: 6,
  fontSizeLG: 15,
  fontSize: 14,
  fontSizeSM: 13,
  fontWeightStrong: 600,
  primaryShadow: '0 2px 8px rgba(217, 119, 6, 0.2)',
  colorPrimaryHover: '#F59E0B',
  defaultBorderColor: '#E0E0DE',
  defaultBg: '#FFFFFF',
};

const inputToken: NonNullable<NonNullable<ThemeConfig['components']>['Input']> = {
  controlHeightLG: 44,
  controlHeight: 40,
  controlHeightSM: 34,
  borderRadius: 8,
  paddingInline: 14,
  paddingInlineLG: 16,
  paddingInlineSM: 12,
  activeBorderColor: '#D97706',
  hoverBorderColor: '#F59E0B',
  activeShadow: '0 0 0 3px rgba(217, 119, 6, 0.12)',
  colorBgContainer: '#FFFFFF',
  colorBorder: '#E0E0DE',
};

const selectToken: NonNullable<NonNullable<ThemeConfig['components']>['Select']> = {
  controlHeightLG: 44,
  controlHeight: 40,
  controlHeightSM: 34,
  borderRadius: 8,
  colorBgContainer: '#FFFFFF',
  colorBorder: '#E0E0DE',
};

const menuToken: NonNullable<NonNullable<ThemeConfig['components']>['Menu']> = {
  itemHeight: 44,
  itemBorderRadius: 8,
  subMenuItemBorderRadius: 8,
  itemPaddingInline: 14,
  itemMarginBlock: 4,
  itemMarginInline: 8,
  iconSize: 18,
  iconMarginInlineEnd: 12,
  activeBarWidth: 3,
  activeBarBorderWidth: 0,
  itemBg: 'transparent',
  itemSelectedBg: 'rgba(217, 119, 6, 0.1)',
  itemSelectedColor: '#D97706',
  itemHoverBg: 'rgba(217, 119, 6, 0.06)',
  itemHoverColor: '#1A1A18',
  itemActiveBg: 'rgba(217, 119, 6, 0.12)',
  itemColor: '#6B6B69',
};

const tableToken: NonNullable<NonNullable<ThemeConfig['components']>['Table']> = {
  headerBg: '#FAFAF8',
  headerColor: '#6B6B69',
  headerSortActiveBg: '#F5F5F3',
  headerSortHoverBg: '#EBEBEA',
  rowHoverBg: 'rgba(217, 119, 6, 0.04)',
  borderColor: '#EBEBEA',
  cellPaddingBlock: 14,
  cellPaddingInline: 16,
  colorBgContainer: '#FFFFFF',
};

const cardToken: NonNullable<NonNullable<ThemeConfig['components']>['Card']> = {
  headerBg: 'transparent',
  colorBgContainer: '#FFFFFF',
  colorBorderSecondary: '#F5F5F3',
  paddingLG: 24,
};

const modalToken: NonNullable<NonNullable<ThemeConfig['components']>['Modal']> = {
  contentBg: '#FFFFFF',
  headerBg: '#FFFFFF',
  titleColor: '#1A1A18',
  borderRadiusLG: 12,
};

const tagToken: NonNullable<NonNullable<ThemeConfig['components']>['Tag']> = {
  defaultBg: '#F5F5F3',
  defaultColor: '#3D3D3B',
};

const dropdownToken: NonNullable<NonNullable<ThemeConfig['components']>['Dropdown']> = {
  colorBgElevated: '#FFFFFF',
  controlItemBgHover: 'rgba(217, 119, 6, 0.06)',
  controlItemBgActive: 'rgba(217, 119, 6, 0.1)',
};

export const getAntdThemeConfig = (): ThemeConfig => ({
  token: seedToken,
  components: {
    Button: buttonToken,
    Input: inputToken,
    Select: selectToken,
    Menu: menuToken,
    Table: tableToken,
    Card: cardToken,
    Modal: modalToken,
    Tag: tagToken,
    Dropdown: dropdownToken,
  },
});
